/**
 * Per-agent integration allowlist (least-privilege) — shared frontend helpers.
 *
 * Given an integration-status payload (the shape returned by both
 * GET /marketplace/workflows/{slug}/integration-status and
 * GET /workflows/{uuid}/integrations/status):
 *
 *   { required, connected, missing, optional, optionalConnected,
 *     optionalMissing, integrations }
 *
 * where each list entry is a provider-id string OR an `{ any: [...] }` OR-group
 * (a CONNECTED OR-group additionally carries `connectedMembers: string[]`).
 *
 * These helpers derive, from that payload:
 *   - connectedProviders(status)  → the flat set of provider ids that are
 *     actually connected (so they can be offered for selection — only connected
 *     integrations make sense in the allowlist; you can't enable a tool whose
 *     integration isn't connected).
 *   - requiredConnectedProviders(status) → the subset of the above that the
 *     workflow REQUIRES (members of a required string / required OR-group that
 *     are connected). These must be locked-ON in the UI and force-included in
 *     the payload — deselecting a required integration would break the agent.
 *
 * Both return Sets. Defensive against missing/partial payloads.
 */

// Pull the flat connected provider ids out of one "connected" list (required's
// `connected` or optional's `optionalConnected`). String entries are connected
// as-is; OR-group entries contribute their `connectedMembers`.
function flattenConnected(list) {
  const out = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    if (typeof entry === 'string') {
      out.add(entry);
    } else if (entry && Array.isArray(entry.connectedMembers)) {
      for (const m of entry.connectedMembers) if (typeof m === 'string') out.add(m);
    }
  }
  return out;
}

/**
 * The no-connection toggleable skills the status feed declares (e.g.
 * codebase-memory) as a flat array of `{ id, name, description, enabled }`. These
 * reuse the SAME allowlist as integration providers but have NO Connect step —
 * they're always "candidates" (no connection gate). Defensive against absent /
 * malformed payloads.
 * @param {object|null} status
 * @returns {Array<{id:string,name?:string,description?:string,enabled?:boolean}>}
 */
export function toggleableSkills(status) {
  if (!status || typeof status !== 'object' || !Array.isArray(status.toggleableSkills)) return [];
  return status.toggleableSkills.filter((s) => s && typeof s.id === 'string');
}

/**
 * All provider ids that are currently connected for this workflow's account —
 * across BOTH the required and optional sections — PLUS every no-connection
 * toggleable skill id (codebase-memory etc). These are the candidates the user
 * can include/exclude in the allowlist. Toggleable skills are always candidates
 * (no connection required), so they're members of this set whenever the workflow
 * declares them.
 * @param {object|null} status
 * @returns {Set<string>}
 */
export function connectedProviders(status) {
  const out = new Set();
  if (!status || typeof status !== 'object') return out;
  for (const id of flattenConnected(status.connected)) out.add(id);
  for (const id of flattenConnected(status.optionalConnected)) out.add(id);
  for (const s of toggleableSkills(status)) out.add(s.id);
  return out;
}

/**
 * The connected provider ids that the workflow REQUIRES (and so must be
 * locked-ON + force-included). Derived from the REQUIRED section's `connected`
 * list only — optional integrations are never forced.
 *
 * NOTE: this is the FLAT set across both bare-required strings AND OR-group
 * members. It is the right "force-include candidates" set for legacy callers,
 * but it is NOT the lock set — an OR-group only needs ≥1 member, so locking
 * every member would wrongly forbid swapping e.g. GitHub↔GitLab. For the
 * lock/selection rules use hardRequiredProviders + requiredGroups + isLocked.
 * @param {object|null} status
 * @returns {Set<string>}
 */
export function requiredConnectedProviders(status) {
  if (!status || typeof status !== 'object') return new Set();
  return flattenConnected(status.connected);
}

/**
 * HARD-required connected providers — the bare-string entries of the required
 * section that are connected. These are always locked ON (deselecting them
 * would break the agent). OR-group members are NOT here (a group only needs ≥1
 * member; see requiredGroups), so they stay individually toggleable.
 * @param {object|null} status
 * @returns {Set<string>}
 */
export function hardRequiredProviders(status) {
  const out = new Set();
  if (!status || typeof status !== 'object') return out;
  const connected = connectedProviders(status);
  for (const entry of Array.isArray(status.required) ? status.required : []) {
    if (typeof entry === 'string' && connected.has(entry)) out.add(entry);
  }
  return out;
}

/**
 * Required `{ any: [...] }` OR-groups, each as the array of its CONNECTED member
 * ids. The selection must keep ≥1 member of each group selected; members are
 * otherwise individually toggleable (so you can swap GitHub↔GitLab). Only groups
 * with at least one connected member are returned (an all-disconnected group is
 * a "missing" deploy gate, not a selection constraint).
 * @param {object|null} status
 * @returns {string[][]}
 */
export function requiredGroups(status) {
  const groups = [];
  if (!status || typeof status !== 'object') return groups;
  const connected = connectedProviders(status);
  for (const entry of Array.isArray(status.required) ? status.required : []) {
    if (entry && typeof entry === 'object' && Array.isArray(entry.any)) {
      const members = entry.any.filter((m) => connected.has(m));
      if (members.length) groups.push(members);
    }
  }
  return groups;
}

/**
 * Is `id` locked ON in the current selection — i.e. deselecting it would violate
 * the schema's required spec? True when it's a hard-required provider, OR when
 * it's the LAST still-selected member of a required OR-group (removing it would
 * drop that group to zero). A group member with a selected sibling is NOT locked
 * — you can free it by keeping the sibling.
 * @param {object|null} status
 * @param {Set<string>} selected   the current working selection
 * @param {string} id
 * @returns {boolean}
 */
export function isLocked(status, selected, id) {
  if (hardRequiredProviders(status).has(id)) return true;
  for (const members of requiredGroups(status)) {
    if (members.includes(id)) {
      const stillSelected = members.filter((m) => selected.has(m));
      if (stillSelected.length === 1 && stillSelected[0] === id) return true;
    }
  }
  return false;
}

/**
 * Compute the initial allowlist selection for a workflow.
 *
 * - When the workflow has NO stored allowlist (`enabledIntegrations` absent /
 *   null) → default ALL-ON: every connected provider AND every no-connection
 *   toggleable skill (codebase-memory etc) is selected (the backward-compatible
 *   "all on" default, now made explicit — toggleable skills default ON too).
 * - When it HAS a stored allowlist → start from that, intersected with what's
 *   actually connected (a provider may have been disconnected since).
 * - In BOTH cases the required-connected providers are force-included (locked).
 *
 * @param {object|null} status               the integration-status payload
 * @param {string[]|null|undefined} stored    the workflow row's enabledIntegrations
 * @returns {Set<string>} the selected provider ids
 */
export function initialSelection(status, stored) {
  const connected = connectedProviders(status);
  let selected;
  if (Array.isArray(stored)) {
    selected = new Set(stored.filter((id) => connected.has(id)));
  } else {
    selected = new Set(connected); // default all-on
  }
  // Force the hard-required (bare) providers in — they can never be off.
  for (const id of hardRequiredProviders(status)) selected.add(id);
  // Each required OR-group needs ≥1 connected member; if a stored allowlist
  // dropped them all, restore the first (keeps the requirement satisfied —
  // mirrors the backend's sanitizeEnabledIntegrations).
  for (const members of requiredGroups(status)) {
    if (!members.some((m) => selected.has(m))) selected.add(members[0]);
  }
  return selected;
}
